import React from "react";
import {
  Template,
  ColorValue,
  TextValue,
  Element,
  SvgElement,
} from "../core/template.schemas";
import {
  resolveStaticOrDynamicWithPreview,
  ResolveContext,
  resolveImageSource,
} from "../core/runtime/resolve";
import {
  EntitySlotSelectionMap,
  SelectorSelectionMap,
} from "../create-post/types/PostGenerationTypes";
import { cn } from "@/lib/utils/cn";
import useImage from "use-image";
import { useGoogleFonts } from "../template-editor/hooks/useGoogleFonts";
import { processSvg } from "../utils/svgUtils";
import { useCacheBustedImage } from "../hooks/useCacheBustedImage";

/**
 * Converts a hex color string to an RGBA string with optional opacity.
 */
function hexToRgba(hex: string, opacity: number = 1): string {
  if (!/^#[0-9a-fA-F]{6}$/.test(hex)) {
    return `rgba(0,0,0,${opacity})`; // Return black with opacity for invalid hex
  }

  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);

  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
}

/**
 * Parses a Google Fonts variant string to extract weight and style
 */
function parseGoogleFontVariant(variant?: string): {
  weight: string;
  style: string;
} {
  if (!variant) return { weight: "400", style: "normal" };

  if (variant === "regular") return { weight: "400", style: "normal" };
  if (variant === "italic") return { weight: "400", style: "italic" };

  if (variant.endsWith("italic")) {
    const weight = variant.replace("italic", "") || "400";
    return { weight, style: "italic" };
  }

  // Pure numeric weight
  return { weight: variant, style: "normal" };
}

interface TemplateRendererProps {
  /** Template to render */
  template: Template;
  /** Optional entity slot selections for dynamic content resolution */
  entitySlots?: EntitySlotSelectionMap;
  /** Optional selector value selections for dynamic content resolution */
  selectorSelections?: SelectorSelectionMap;
  /** Optional override selections for dynamic overrides */
  overrides?: Record<string, any>;
  /** Optional manual attribute overrides (by slot key) */
  manualOverrides?: Record<string, Record<string, any>>;
  /** Maximum size for the rendered template (scales to fit) */
  maxSize?: number;
  /** Fit the template to the container width */
  fitToContainer?: boolean;
  resizeMode?: "width" | "contain" | "cover";
  containerSize?: { width: number; height: number };
  /** Additional CSS classes */
  className?: string;
  /** Custom styling for the container */
  style?: React.CSSProperties;
  /** Whether to show a border around the template */
  showBorder?: boolean;
  /** Background color for the container */
  backgroundColor?: string;
  /** Corner radius for the container */
  cornerRadius?: number;
  /** Shadow style for the container */
  shadow?: "none" | "sm" | "md" | "lg";
  /** Whether to enable hover effects */
  enableHover?: boolean;
  /** Vertical alignment of the content within the container */
  verticalAlign?: "center" | "top" | "bottom";
  /** Callback when the template is clicked */
  onClick?: () => void;
}

/**
 * Unified Template Renderer Component
 *
 * This component provides a consistent way to render templates across the application.
 * It supports:
 * - Static and dynamic content resolution using resolveStaticOrDynamicWithPreview
 * - Entity slot selections for populated previews
 * - Flexible sizing and styling options
 * - Proper gradient and color rendering
 * - Performance optimizations with memoization
 */

const FitTextRenderer: React.FC<{
  content: string;
  textStyles: React.CSSProperties;
  ensureFontLoaded: (fontFamily: string, fontVariant?: string) => void;
  fontFamily: string;
  fontVariant: string;
}> = ({ content, textStyles, ensureFontLoaded, fontFamily, fontVariant }) => {
  const [fontSize, setFontSize] = React.useState(1);

  React.useEffect(() => {
    ensureFontLoaded(fontFamily, fontVariant);

    const targetWidth = Number(textStyles.width);
    const targetHeight = Number(textStyles.height);

    if (!targetWidth || !targetHeight || !content) {
      setFontSize(1);
      return;
    }

    const measureElement = document.createElement("div");
    document.body.appendChild(measureElement);

    Object.assign(measureElement.style, {
      fontFamily: textStyles.fontFamily,
      fontWeight: textStyles.fontWeight as any,
      fontStyle: textStyles.fontStyle as any,
      lineHeight: textStyles.lineHeight as any,
      width: `${targetWidth}px`,
      position: "absolute",
      left: "-9999px",
      top: "-9999px",
      height: "auto",
      whiteSpace: "normal",
      wordBreak: "break-word",
    });
    measureElement.textContent = content;

    let min = 1;
    let max = targetHeight + 10;
    let bestSize = 1;

    while (min <= max) {
      const mid = Math.floor((min + max) / 2);
      if (mid < 1) break;

      measureElement.style.fontSize = `${mid}px`;

      if (measureElement.scrollHeight <= targetHeight) {
        bestSize = mid;
        min = mid + 1;
      } else {
        max = mid - 1;
      }
    }

    document.body.removeChild(measureElement);
    setFontSize(bestSize);
  }, [content, textStyles, ensureFontLoaded, fontFamily, fontVariant]);

  return (
    <div style={{ ...textStyles, fontSize: `${fontSize}px` }}>{content}</div>
  );
};

/**
 * Image Renderer Component
 *
 * Separate component to handle image rendering with useImage hook
 * This follows React hooks rules by calling useImage at component level
 */
const ImageRenderer: React.FC<{
  element: any;
  resolveContext: ResolveContext;
  baseStyle: React.CSSProperties;
  scale: number;
}> = ({ element, resolveContext, baseStyle, scale }) => {
  const resolvedSource = resolveImageSource(element.source, resolveContext);
  const [image, status] = useCacheBustedImage(resolvedSource, "anonymous");
  const [fallbackImage] = useImage(
    "/defaults/image_placeholder.png",
    "anonymous",
  );
  const img = status === "loaded" ? image : fallbackImage;

  const fit: "fill" | "contain" | "cover" | "none" =
    (element.source as any)?.format?.fit || "cover";
  const overflow: "hidden" | "visible" = element.overflow || "hidden";
  const objectPosition = fit === "none" ? "left top" : "center";

  // Extract filters to apply them on the outer wrapper (avoid clipping)
  const { filter, ...layoutStyle } = baseStyle as any;

  // Render wrapper with position/size and effects
  // Never apply shadow filter here, it goes to the image
  const wrapperStyle = {
    ...layoutStyle,
    filter: undefined,
  };

  const innerStyle = {
    width: "100%",
    height: "100%",
    overflow,
    borderRadius: element.clipRadius
      ? Math.round(element.clipRadius * scale)
      : undefined,
    backgroundColor: !img ? "#f0f0f0" : undefined,
  };

  if (!img) {
    return (
      <div style={wrapperStyle}>
        <div style={innerStyle} />
      </div>
    );
  }

  return (
    <div style={wrapperStyle}>
      <div style={innerStyle}>
        <img
          src={img.src}
          crossOrigin="anonymous"
          alt=""
          style={{
            position: "absolute",
            left: 0,
            top: 0,
            width: "100%",
            height: "100%",
            objectFit: fit,
            objectPosition,
            display: "block",
            // Always apply shadow to the image
            filter,
          }}
        />
      </div>
    </div>
  );
};

const SvgElementRenderer: React.FC<{
  element: SvgElement;
  resolveContext: ResolveContext;
  baseStyle: React.CSSProperties;
}> = ({ element, resolveContext, baseStyle }) => {
  const [svgUrl, setSvgUrl] = React.useState<string | null>(null);
  const [rawSvg, setRawSvg] = React.useState<string | null>(null);
  const src = resolveImageSource(element.source, resolveContext);
  const [svgImage, svgStatus] = useCacheBustedImage(src, "anonymous");

  // 1. Fetch SVG content only when src changes
  React.useEffect(() => {
    setRawSvg(null);

    if (!src || svgStatus !== "loaded" || !svgImage) {
      return;
    }

    let isActive = true;
    fetch(svgImage.src)
      .then((res) => {
        if (!res.ok) throw new Error("Failed to fetch SVG from blob URL");
        return res.blob();
      })
      .then((blob) => blob.text())
      .then((text) => {
        if (isActive) setRawSvg(text);
      })
      .catch((err) => {
        console.warn(`Failed to load SVG for element ${element.id}`, err);
        if (isActive) setRawSvg(null);
      });

    return () => {
      isActive = false;
    };
  }, [src, element.id, svgStatus, svgImage]);

  // 2. Process SVG when rawSvg or dynamic properties change
  React.useEffect(() => {
    if (!rawSvg) {
      setSvgUrl(null);
      return;
    }

    const monochromeColor =
      element.fillMode === "monochrome" && element.monochromeColor
        ? resolveStaticOrDynamicWithPreview(
            element.monochromeColor,
            resolveContext,
          )
        : undefined;

    const colorMapping: Record<string, string> = {};
    if (element.colorMapping) {
      Object.entries(element.colorMapping).forEach(([key, val]) => {
        const resolved = resolveStaticOrDynamicWithPreview(val, resolveContext);
        if (resolved) {
          colorMapping[key.toLowerCase()] = resolved;
        }
      });
    }

    const processed = processSvg(rawSvg, {
      fillMode: element.fillMode || "original",
      monochromeColor,
      colorMapping,
    });

    const blob = new Blob([processed], { type: "image/svg+xml" });
    const url = URL.createObjectURL(blob);
    setSvgUrl(url);

    return () => {
      URL.revokeObjectURL(url);
    };
  }, [
    rawSvg,
    element.fillMode,
    element.monochromeColor,
    element.colorMapping,
    resolveContext,
  ]);

  if (!svgUrl) {
    return <div style={{ ...baseStyle, backgroundColor: "#f0f0f0" }} />;
  }

  return <img src={svgUrl} style={baseStyle} alt="" />;
};

export const TemplateRenderer: React.FC<TemplateRendererProps> = ({
  template,
  entitySlots,
  selectorSelections,
  overrides,
  manualOverrides,
  maxSize = 120,
  fitToContainer = true,
  resizeMode = "width",
  containerSize,
  className = "",
  style,
  showBorder = true,
  backgroundColor = "#ffffff",
  cornerRadius = 8,
  shadow = "sm",
  enableHover = false,
  verticalAlign = "center",
  onClick,
}) => {
  const { canvas, elements } = template;
  const { loadFont } = useGoogleFonts();

  const containerRef = React.useRef<HTMLDivElement>(null);
  const [internalContainerSize, setInternalContainerSize] = React.useState<{
    width: number;
    height: number;
  }>({
    width: 0,
    height: 0,
  });
  const hasUsableExternalContainerSize = Boolean(
    containerSize &&
    containerSize.width > 0 &&
    (resizeMode === "width" || containerSize.height > 0),
  );
  const effectiveContainerSize = hasUsableExternalContainerSize
    ? containerSize!
    : internalContainerSize;

  React.useEffect(() => {
    if (hasUsableExternalContainerSize) return;

    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const rect = entries[0]?.contentRect;
      setInternalContainerSize({
        width: rect?.width ?? 0,
        height: rect?.height ?? 0,
      });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [hasUsableExternalContainerSize]);

  // Create resolve context with entity slots
  const resolveContext: ResolveContext = React.useMemo(() => {
    const selectedSlots: { [slotId: string]: any } = {};
    const selectedSelectors: { [selectorId: string]: any } = {};
    const manualSlotOverridesById: Record<string, Record<string, any>> = {};

    if (entitySlots) {
      // Map entity slots to the format expected by resolveContext
      Object.entries(entitySlots).forEach(([slotKey, slot]) => {
        if (slot.selectedInstance && slot.slotId) {
          // Use the specific slotId instead of searching by entityType
          selectedSlots[slot.slotId] = slot.selectedInstance.data;
        }

        // Map manual overrides for this slot if they exist
        if (manualOverrides && manualOverrides[slotKey] && slot.slotId) {
          if (!manualSlotOverridesById[slot.slotId]) {
            manualSlotOverridesById[slot.slotId] = {};
          }
          Object.assign(
            manualSlotOverridesById[slot.slotId],
            manualOverrides[slotKey],
          );
        }
      });
    }
    if (selectorSelections) {
      Object.values(selectorSelections).forEach((sel) => {
        selectedSelectors[sel.selectorId] =
          sel.selectedValue ?? sel.defaultValue ?? undefined;
      });
    }

    return {
      selected: { slots: selectedSlots, selectors: selectedSelectors },
      overrides: overrides || {},
      manualSlotOverrides: manualSlotOverridesById,
      templateSlots: template.slots || [],
    };
  }, [
    entitySlots,
    selectorSelections,
    overrides,
    manualOverrides,
    template.slots,
  ]);

  // Helper functions for value resolution
  const resolveColor = React.useCallback(
    (colorValue: ColorValue, opacity?: number): string => {
      try {
        const hex = resolveStaticOrDynamicWithPreview(
          colorValue,
          resolveContext,
        );
        // Use nullish coalescing to default undefined/null opacity to 1
        return hexToRgba(hex, opacity ?? 1);
      } catch (error) {
        console.warn("Failed to resolve color value:", error);
        return "rgba(0,0,0,1)"; // Fallback color
      }
    },
    [resolveContext],
  );

  const resolveText = React.useCallback(
    (textValue: TextValue): string => {
      try {
        return resolveStaticOrDynamicWithPreview(textValue, resolveContext);
      } catch (error) {
        console.warn("Failed to resolve text value:", error);
        return "Text"; // Fallback text
      }
    },
    [resolveContext],
  );

  // Calculate optimal scale factor
  const scale = React.useMemo(() => {
    if (fitToContainer && effectiveContainerSize.width > 0) {
      if (resizeMode === "contain") {
        if (effectiveContainerSize.height <= 0)
          return effectiveContainerSize.width / canvas.width;
        return Math.min(
          effectiveContainerSize.width / canvas.width,
          effectiveContainerSize.height / canvas.height,
        );
      }

      if (resizeMode === "cover") {
        if (effectiveContainerSize.height <= 0)
          return effectiveContainerSize.width / canvas.width;
        return Math.max(
          effectiveContainerSize.width / canvas.width,
          effectiveContainerSize.height / canvas.height,
        );
      }

      return effectiveContainerSize.width / canvas.width;
    }
    const canvasAspect = canvas.width / canvas.height;
    return canvasAspect > 1 ? maxSize / canvas.width : maxSize / canvas.height;
  }, [
    canvas.height,
    canvas.width,
    effectiveContainerSize.height,
    effectiveContainerSize.width,
    fitToContainer,
    maxSize,
    resizeMode,
  ]);

  const previewWidth = Math.max(1, Math.round(canvas.width * scale));
  const previewHeight = Math.max(1, Math.round(canvas.height * scale));

  // Helper function to create background styles (Single Responsibility Principle)
  const createBackgroundStyle = React.useCallback(
    (fill?: any): React.CSSProperties => {
      if (!fill) return {};

      if (fill.kind === "solid") {
        return { backgroundColor: resolveColor(fill.color, fill.opacity) };
      }

      if (fill.kind === "gradient" && fill.stops) {
        const gradientStops = fill.stops
          .map(
            (stop: any) =>
              `${resolveColor(stop.color, stop.opacity)} ${stop.offset * 100}%`,
          )
          .join(", ");

        const gradientValue =
          fill.gradientType === "radial"
            ? `radial-gradient(circle farthest-side, ${gradientStops})`
            : `linear-gradient(${fill.angle ?? 90}deg, ${gradientStops})`;

        return { background: gradientValue };
      }

      return {};
    },
    [resolveColor],
  );

  // Helper function to create border styles (Single Responsibility Principle)
  const createBorderStyle = React.useCallback(
    (stroke?: any): React.CSSProperties => {
      if (!stroke) return {};

      const borderWidth = Math.max(1, Math.round(stroke.width * scale));
      const borderColor = resolveColor(stroke.color);

      return { border: `${borderWidth}px solid ${borderColor}` };
    },
    [scale, resolveColor],
  );

  // Helper function to create base element positioning styles (Single Responsibility Principle)
  // createElementBaseStyle (präzise Positionierung ohne Rounding)
  // Hilfsfunktion für Floating-Point-Nähe zu Null
  const snapToZero = (n: number) => (Math.abs(n) < 1e-9 ? 0 : n);

  const createElementBaseStyle = React.useCallback(
    (element: Element): React.CSSProperties => {
      const style: React.CSSProperties = {
        position: "absolute",
        left: snapToZero(element.x * scale),
        top: snapToZero(element.y * scale),
        width: element.width * scale,
        height: element.height * scale,
        transform: element.rotation
          ? `rotate(${element.rotation}deg)`
          : undefined,
        transformOrigin: element.rotation ? "center" : undefined,
        zIndex: element.zIndex || 0,
        opacity: element.opacity ?? 1,
      };

      // Apply Effects
      if (element.effects && element.effects.length > 0) {
        const dropShadow = element.effects.find(
          (e: any) => e.type === "drop-shadow",
        ) as any;
        const layerBlur = element.effects.find(
          (e: any) => e.type === "layer-blur",
        ) as any;

        if (dropShadow) {
          const x = Math.round(dropShadow.x * scale);
          const y = Math.round(dropShadow.y * scale);
          // Align HTML shadow blur with Konva/Canvas (Canvas blur is approx 0.5x visual strength of CSS blur)
          const blur = Math.round(dropShadow.blur * scale * 0.5);
          const color = resolveColor(dropShadow.color, dropShadow.opacity);

          // For Text and Image, use drop-shadow filter
          if (element.type === "text" || element.type === "image") {
            style.filter = [
              style.filter || "",
              `drop-shadow(${x}px ${y}px ${blur}px ${color})`,
            ]
              .filter(Boolean)
              .join(" ");
          } else {
            // For Rect/Circle, use box-shadow
            style.boxShadow = `${x}px ${y}px ${blur}px ${color}`; // No spread support per Konva parity request
          }
        }

        if (layerBlur) {
          // Align HTML blur strength with Konva (Canvas blur is approx 0.5x visual strength of CSS blur)
          const radius = Math.round(layerBlur.radius * scale * 0.2);
          style.filter = [style.filter || "", `blur(${radius}px)`]
            .filter(Boolean)
            .join(" ");
        }
      }

      return style;
    },
    [scale, resolveColor],
  );

  // Helper function to load fonts for text elements
  const ensureFontLoaded = React.useCallback(
    (fontFamily: string, fontVariant?: string) => {
      if (
        !fontFamily ||
        fontFamily === "Inter" ||
        fontFamily.includes("system-ui")
      ) {
        return; // Skip system fonts
      }

      try {
        // Load the specific variant or default to regular
        const variant = fontVariant || "regular";
        loadFont(fontFamily, [variant]);
      } catch (error) {
        console.warn("Failed to load font:", fontFamily, error);
      }
    },
    [loadFont],
  );

  // Render individual element
  const renderElement = React.useCallback(
    (element: Element) => {
      const baseStyle = createElementBaseStyle(element);

      switch (element.type) {
        case "rect": {
          const backgroundStyle = createBackgroundStyle((element as any).fill);
          const borderStyle = createBorderStyle((element as any).stroke);
          return (
            <div
              key={element.id}
              style={{
                ...baseStyle,
                ...backgroundStyle,
                ...borderStyle,
                borderRadius: (element as any).cornerRadius
                  ? Math.round((element as any).cornerRadius * scale)
                  : undefined,
              }}
            />
          );
        }

        case "circle": {
          const backgroundStyle = createBackgroundStyle((element as any).fill);
          const borderStyle = createBorderStyle((element as any).stroke);
          // Circle element.x/y is Top-Left (consistent with other elements).
          // HTML div is Top-Left origin, so we use baseStyle directly.
          // Previously we subtracted width/2, which was incorrect if x/y is Top-Left.
          return (
            <div
              key={element.id}
              style={{
                ...baseStyle,
                ...backgroundStyle,
                ...borderStyle,
                borderRadius: "50%",
              }}
            />
          );
        }

        case "text": {
          const textElement = element as any;
          const content = resolveText(textElement.content);

          const fontVariant =
            textElement.fontVariant ||
            (textElement.fontWeight !== "400" ||
            textElement.fontStyle !== "normal"
              ? `${textElement.fontWeight}${textElement.fontStyle === "italic" ? "italic" : ""}`
              : "regular");

          const { weight, style: fontStyle } =
            parseGoogleFontVariant(fontVariant);
          ensureFontLoaded(textElement.fontFamily, fontVariant);

          const lineHeight = textElement.lineHeight || 1.2;
          // Calculate max lines that fit in the container
          const fontSizePx = (textElement.fontSizing?.fontSize || 16) * scale;
          const lineHeightPx = fontSizePx * lineHeight;
          const containerHeight = element.height * scale;
          // Ensure at least 1 line is shown, floor to avoid partial lines if desired,
          // or use a generous estimate. Since we want to hide lines that don't fit:
          const maxLines = Math.max(
            1,
            Math.floor(containerHeight / lineHeightPx),
          );

          const textStyles: React.CSSProperties = {
            ...baseStyle,
            color: resolveColor(textElement.color),
            fontFamily:
              textElement.fontFamily || "Inter, system-ui, sans-serif",
            fontWeight: weight,
            fontStyle: fontStyle,
            display: "flex",
            alignItems:
              textElement.align?.v === "center"
                ? "center"
                : textElement.align?.v === "bottom"
                  ? "flex-end"
                  : "flex-start",
            justifyContent:
              textElement.align?.h === "center"
                ? "center"
                : textElement.align?.h === "right"
                  ? "flex-end"
                  : "flex-start",
            textAlign: textElement.align?.h || "left",
            lineHeight: lineHeight,
            overflow: "hidden",
            wordBreak: "break-word",
          };

          if (textElement.fontSizing?.mode === "fit") {
            // The FitTextRenderer component is not defined in this file, but the instruction
            // requires its use. We assume it's available in the project.
            // This replaces the simplified 'fit' logic with a dedicated component.
            return (
              <FitTextRenderer
                key={element.id}
                content={content}
                textStyles={textStyles}
                ensureFontLoaded={ensureFontLoaded}
                fontFamily={textElement.fontFamily || "Inter"}
                fontVariant={fontVariant}
              />
            );
          } else {
            // Manual font sizing logic
            const finalStyles: React.CSSProperties = { ...textStyles };
            finalStyles.fontSize = Math.max(
              4,
              Math.round((textElement.fontSizing?.fontSize || 16) * scale),
            );

            if (textElement.fontSizing?.ellipsis) {
              return (
                <div
                  key={element.id}
                  style={{ ...finalStyles, overflow: "hidden" }}
                >
                  <span
                    style={{
                      whiteSpace: "nowrap",
                      textOverflow: "ellipsis",
                      overflow: "hidden",
                      wordBreak: "normal",
                      minWidth: 0, // Allow flex item to shrink for ellipsis
                      flexShrink: 1, // Allow the span to shrink
                    }}
                  >
                    {content}
                  </span>
                </div>
              );
            } else {
              // Apply word-break only when no ellipsis and overflow is handled by the parent div
              finalStyles.wordBreak = "break-word";
              finalStyles.overflow = "visible"; // Allow descenders to be visible outside the box

              return (
                <div key={element.id} style={finalStyles}>
                  <div
                    style={{
                      maxHeight: `${maxLines * lineHeight}em`,
                      overflow: "hidden",
                      width: "100%",
                    }}
                  >
                    {content}
                  </div>
                </div>
              );
            }
          }
        }
        case "image": {
          const imageElement = element as any;

          return (
            <ImageRenderer
              key={element.id}
              element={imageElement}
              resolveContext={resolveContext}
              baseStyle={baseStyle}
              scale={scale}
            />
          );
        }

        case "svg": {
          const svgElement = element as SvgElement;
          return (
            <SvgElementRenderer
              key={element.id}
              element={svgElement}
              resolveContext={resolveContext}
              baseStyle={baseStyle}
            />
          );
        }

        default:
          return null;
      }
    },
    [
      createElementBaseStyle,
      createBackgroundStyle,
      createBorderStyle,
      scale,
      resolveText,
      ensureFontLoaded,
      resolveColor,
      resolveContext,
    ],
  );

  // Memoize rendered elements for performance
  const renderedElements = React.useMemo(() => {
    return elements
      .filter((element) => element.visible !== false)
      .sort((a, b) => (a.zIndex || 0) - (b.zIndex || 0))
      .map(renderElement);
  }, [elements, renderElement]);

  // Shadow classes
  const shadowClass = {
    none: "",
    sm: "shadow-sm",
    md: "shadow-md",
    lg: "shadow-lg",
  }[shadow];

  const previewBoxClassName = cn(
    "relative overflow-hidden",
    showBorder && "border border-gray-200",
    shadowClass,
    enableHover && "transition-all duration-200 hover:shadow-md",
    onClick && "cursor-pointer",
    className,
  );

  const previewBoxStyle: React.CSSProperties = {
    width: fitToContainer && resizeMode === "width" ? "100%" : previewWidth,
    height: previewHeight,
    backgroundColor,
    borderRadius: cornerRadius,
    ...style,
  };

  if (fitToContainer && resizeMode !== "width") {
    const alignmentClass = {
      center: "items-center",
      top: "items-start",
      bottom: "items-end",
    }[verticalAlign];

    return (
      <div
        className={`w-full h-full min-w-0 min-h-0 flex ${alignmentClass} justify-center`}
        ref={containerRef}
        onClick={onClick}
      >
        <div className={previewBoxClassName} style={previewBoxStyle}>
          {renderedElements}
        </div>
      </div>
    );
  }

  return (
    <div
      className={previewBoxClassName}
      style={previewBoxStyle}
      ref={containerRef}
      onClick={onClick}
    >
      {renderedElements}
    </div>
  );
};

// Export convenience wrapper for backward compatibility
export const TemplatePreview: React.FC<{
  template: Template;
  className?: string;
  maxSize?: number;
  entitySlots?: EntitySlotSelectionMap;
  backgroundColor?: string;
}> = ({ template, className, maxSize, entitySlots, backgroundColor }) => (
  <TemplateRenderer
    template={template}
    className={className}
    maxSize={maxSize}
    entitySlots={entitySlots}
    backgroundColor={backgroundColor}
  />
);
